import React, { useState, useMemo } from "react";
import { isFunction } from "lodash";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Button,
  DialogActions,
  DialogContentText,
  Paper,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import PaymentForm from "./PaymentForm";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

/**
 * Processes the creation of a Stripe Payment Intent.
 * This function encapsulates the common pattern of dispatching an action
 * to create a payment intent and handling its response.
 * @param {object} params - The parameters object.
 * @param {function} [params.dispatch] - The Redux dispatch function (optional if createPaymentIntentAction is a direct function).
 * @param {function} params.createPaymentIntentAction - The Redux action creator function for creating a payment intent.
 *                                                      If dispatch is not provided, this should be a function that returns a Promise.
 * @param {object} params.paymentDetails - Object containing details for the payment intent
 *                                         (e.g., amount, plan_type, linked_table, linked_id).
 * @param {function} [params.setLoading] - Function to set the loading state in the parent component (e.g., (isLoading) => void).
 * @param {function} [params.onSuccess] - Callback function if payment intent is created successfully.
 *                                      Receives an object with payment details.
 * @param {function} [params.onError] - Callback function if an error occurs.
 *                                    Receives an error message: (errorMessage: string) => void.
 * @returns {Promise<void>}
 */
export const processStripePaymentIntent = async ({
  dispatch,
  createPaymentIntentAction,
  paymentDetails,
  setLoading,
  onSuccess,
  onError,
}) => {
  if (isFunction(setLoading)) {
    setLoading(true);
  }

  try {
    const response = isFunction(dispatch)
      ? await dispatch(createPaymentIntentAction(paymentDetails))
      : await createPaymentIntentAction(paymentDetails);

    if (response && response.data && response.data.status) {
      if (isFunction(onSuccess)) {
        const responseData = response.data.data || {};
        // Check if this is a free plan - server will return free_plan: true
        if (responseData.free_plan) {
          log.debug("Free plan detected, skipping payment dialog");
          onSuccess({
            ...responseData,
            free_plan: true,
            paymentId: responseData.payment_id,
            applicationId:
              responseData.application_id ||
              (paymentDetails.linked_table === "online_application"
                ? paymentDetails.linked_id
                : null),
            application_id:
              responseData.application_id ||
              (paymentDetails.linked_table === "online_application"
                ? paymentDetails.linked_id
                : null),
            paymentAmount: 0,
          });
        } else {
          // Normal paid plan flow
          onSuccess({
            clientSecret: responseData.client_secret,
            paymentId: responseData.payment_id,
            paymentIntentId: responseData.payment_intent_id,
            paymentAmount: paymentDetails.amount,
          });
        }
      }
    } else {
      const errorMessage =
        (response && response.data && response.data.message) ||
        "Failed to initialize payment. Please try again.";
      if (isFunction(onError)) {
        onError(errorMessage);
      } else {
        log.error("Payment Intent Error:", errorMessage);
      }
    }
  } catch (error) {
    log.error("Error processing Stripe Payment Intent:", error);
    const errorMessage =
      "An unexpected error occurred while initiating payment. Please try again.";
    if (isFunction(onError)) {
      onError(errorMessage);
    } else {
      log.error("Payment Intent Catch Error:", errorMessage);
    }
  } finally {
    if (isFunction(setLoading)) {
      setLoading(false);
    }
  }
};

/**
 * During payment intent creation, show a loader to indicate that the process is ongoing.
 * @param {boolean} param0.show - Whether to show the loader
 * @returns {JSX.Element}
 * @component
 */
export const PaymentIntentLoader = ({
  show,
  title = "Initializing Payment...",
}) => {
  return !show ? null : (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1301, // Higher than MUI Dialog default zIndex (1300)
      }}
    >
      <Paper
        sx={{
          p: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: 2,
        }}
      >
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography>{title}</Typography>
      </Paper>
    </Box>
  );
};

/**
 * StripePaymentModal component
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Function to close the dialog
 * @param {string} props.clientSecret - The client secret from Stripe PaymentIntent
 * @param {number} props.amount - The payment amount
 * @param {object} [props.stripePromise] - Optional override for the Stripe Promise
 * @param {function} [props.onPaymentProcess] - Optional callback for payment processing state
 * @param {object} [props.successParams] - Optional parameters for PaymentForm on success
 * @param {string} [props.dialogTitle] - Optional title for the dialog
 * @returns {JSX.Element}
 * @component
 */
const StripePaymentModal = ({
  open,
  onClose,
  clientSecret,
  amount,
  stripePromise: customStripePromise,
  onPaymentProcess,
  successParams,
  dialogTitle = "Complete Your Payment",
}) => {
  const [confirmClose, setConfirmClose] = useState(false);

  // Use provided stripePromise if available, otherwise use the default
  const stripeInstance = customStripePromise || stripePromise;

  // Memoize options so Elements doesn't re-initialize on every parent render
  const elementsOptions = useMemo(
    () => ({
      clientSecret,
      appearance: {
        theme: "stripe", // or 'night', 'flat'
        // variables: { colorPrimary: '#0570de' },
      },
    }),
    [clientSecret],
  );

  const handleCloseClick = () => {
    if (clientSecret && !confirmClose) {
      setConfirmClose(true);
    } else {
      handleConfirmClose();
    }
  };

  const handleConfirmClose = () => {
    setConfirmClose(false);
    if (isFunction(onClose)) {
      onClose();
    }
  };

  const handleCancelClose = () => {
    setConfirmClose(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick" && clientSecret) {
            setConfirmClose(true);
          } else if (!clientSecret) {
            handleConfirmClose();
          }
        }}
        aria-labelledby="stripe-payment-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="stripe-payment-dialog-title">
          {dialogTitle}
          <IconButton
            aria-label="close"
            onClick={handleCloseClick}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {!stripeInstance && (
            <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
              <Typography color="error">
                Stripe is not initialized. Please try again later.
              </Typography>
            </Box>
          )}
          {stripeInstance && !clientSecret && (
            <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
              <CircularProgress sx={{ mr: 2 }} />
              <Typography>Initializing payment...</Typography>
            </Box>
          )}
          {stripeInstance && clientSecret && (
            <Elements
              key={clientSecret}
              stripe={stripeInstance}
              options={elementsOptions}
            >
              <PaymentForm
                amount={amount}
                clientSecret={clientSecret}
                onPaymentProcess={onPaymentProcess}
                successParams={successParams}
                onCloseDialog={handleCloseClick}
              />
            </Elements>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmClose}
        onClose={handleCancelClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Cancel Payment Process?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            If you close this window, your payment process will be canceled and
            you will need to restart it. Are you sure you want to exit?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelClose}
            color="primary"
            variant="outlined"
          >
            No, Continue Payment
          </Button>
          <Button
            onClick={handleConfirmClose}
            color="error"
            variant="contained"
            autoFocus
          >
            Yes, Cancel Payment
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StripePaymentModal;
